import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { auth, onAuthChange } from "../api/client";
import { imageToPdf, inspectPdf, MAX_FILE_BYTES, MAX_FILES, PdfError, toBlobUrl } from "../lib/pdf";
import { loadFileRecords, saveFileRecords, sessionGet, sessionSet } from "../lib/storage";

export const DEFAULT_CONFIG = {
  numOfCopies: 1,
  printType: "BW", // BW | Color | Both
  isBothSide: false,
  isBothSideCustom: false,
  pageSize: "A4",
  landscape: false,
  pagesPerSheet: 1,
  scale: 100,
  singleBW: "",
  singleColor: "",
  bothBW: "",
  bothColor: "",
  mixed: "",
};

const PrintFlowContext = createContext(null);

let idCounter = 0;
const newId = () => `${Date.now().toString(36)}-${(idCounter += 1)}`;

function readAsBytes(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

function pdfName(name) {
  const base = String(name || "document").toLowerCase().replace(/\.(pdf|jpg|jpeg|png)$/i, "");
  return `${base}.pdf`;
}

export function PrintFlowProvider({ children }) {
  const [files, setFiles] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [location, setLocationState] = useState(() => sessionGet("location"));
  // Set when the shop came from a QR code: the flow then skips the shop step.
  const [fixedShop, setFixedShop] = useState(() => !!sessionGet("fixedShop"));
  const [session, setSession] = useState(() => ({
    isLoggedIn: auth.isLoggedIn(),
    mobile: auth.getMobile(),
    profile: auth.getProfile(),
  }));
  const filesRef = useRef(files);
  filesRef.current = files;

  // Restore files from IndexedDB once.
  useEffect(() => {
    let cancelled = false;
    loadFileRecords().then((records) => {
      if (cancelled) return;
      const restored = records
        .filter((r) => r && r.bytes && r.bytes.byteLength)
        .map((r) => ({
          id: r.id,
          name: r.name,
          size: r.size,
          pageCount: r.pageCount,
          bytes: r.bytes instanceof Uint8Array ? r.bytes : new Uint8Array(r.bytes),
          config: { ...DEFAULT_CONFIG, ...(r.config || {}) },
          blobUrl: toBlobUrl(r.bytes),
        }));
      setFiles(restored);
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist files whenever they change (after hydration).
  useEffect(() => {
    if (!hydrated) return;
    const handle = setTimeout(() => {
      saveFileRecords(files.map(({ id, name, size, pageCount, bytes, config }) => ({ id, name, size, pageCount, bytes, config })));
    }, 250);
    return () => clearTimeout(handle);
  }, [files, hydrated]);

  useEffect(
    () =>
      onAuthChange(() =>
        setSession({ isLoggedIn: auth.isLoggedIn(), mobile: auth.getMobile(), profile: auth.getProfile() })
      ),
    []
  );

  /** Adds browser File objects; returns { added: [...], errors: [...] }. */
  const addFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList || []);
    const errors = [];
    const added = [];
    const room = MAX_FILES - filesRef.current.length;
    if (incoming.length > room) {
      errors.push(`You can add up to ${MAX_FILES} files per order.`);
    }
    for (const file of incoming.slice(0, Math.max(0, room))) {
      const lower = (file.name || "").toLowerCase();
      const isPdf = file.type === "application/pdf" || lower.endsWith(".pdf");
      const isImage = /^image\/(jpeg|jpg|png)$/.test(file.type) || /\.(jpe?g|png)$/.test(lower);
      if (!isPdf && !isImage) {
        errors.push(`${file.name}: only PDF, JPG and PNG files are supported.`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        errors.push(`${file.name}: files must be smaller than 50 MB.`);
        continue;
      }
      try {
        let bytes = await readAsBytes(file);
        if (isImage) bytes = await imageToPdf(bytes, lower.endsWith(".png") || file.type === "image/png" ? "image/png" : "image/jpeg");
        const { pageCount } = await inspectPdf(bytes);
        const record = {
          id: newId(),
          name: pdfName(file.name),
          size: bytes.byteLength,
          pageCount,
          bytes,
          config: { ...DEFAULT_CONFIG },
          blobUrl: toBlobUrl(bytes),
        };
        added.push(record);
      } catch (e) {
        errors.push(`${file.name}: ${e instanceof PdfError ? e.message : "could not be processed."}`);
      }
    }
    if (added.length) setFiles((prev) => [...prev, ...added]);
    return { added, errors };
  }, []);

  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target && target.blobUrl) URL.revokeObjectURL(target.blobUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const updateConfig = useCallback((id, patch) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, config: { ...f.config, ...patch } } : f)));
  }, []);

  /** Copies layout settings (not page ranges) from one file to every other file. */
  const applyConfigToAll = useCallback((sourceId) => {
    setFiles((prev) => {
      const source = prev.find((f) => f.id === sourceId);
      if (!source) return prev;
      const { numOfCopies, printType, isBothSide, isBothSideCustom, pageSize, landscape, pagesPerSheet, scale } = source.config;
      return prev.map((f) => {
        if (f.id === sourceId) return f;
        const hasCustomRanges = f.config.singleBW || f.config.singleColor || f.config.bothBW || f.config.bothColor || f.config.mixed;
        if (hasCustomRanges) return f;
        const patch = { numOfCopies, pageSize, landscape, pagesPerSheet, scale };
        // Colour "Both" and custom sides need per-file page ranges, so only copy simple modes.
        if (printType !== "Both" && !isBothSideCustom) Object.assign(patch, { printType, isBothSide, isBothSideCustom: false });
        return { ...f, config: { ...f.config, ...patch } };
      });
    });
  }, []);

  const clearFiles = useCallback(() => {
    setFiles((prev) => {
      prev.forEach((f) => f.blobUrl && URL.revokeObjectURL(f.blobUrl));
      return [];
    });
  }, []);

  const setLocation = useCallback((loc, fixed = false) => {
    setLocationState(loc);
    sessionSet("location", loc);
    setFixedShop(fixed);
    sessionSet("fixedShop", fixed);
  }, []);

  const value = useMemo(
    () => ({ files, hydrated, addFiles, removeFile, updateConfig, applyConfigToAll, clearFiles, location, setLocation, fixedShop, session }),
    [files, hydrated, addFiles, removeFile, updateConfig, applyConfigToAll, clearFiles, location, setLocation, fixedShop, session]
  );

  return <PrintFlowContext.Provider value={value}>{children}</PrintFlowContext.Provider>;
}

export function usePrintFlow() {
  const ctx = useContext(PrintFlowContext);
  if (!ctx) throw new Error("usePrintFlow must be used inside PrintFlowProvider");
  return ctx;
}
